import logoVariants from '@/assets/img/logo';
import { MAX_CONTAINER_WIDTH } from '@/constants/dimensions';
import appRoutes, { contentCreatorRoutes } from '@/constants/routes/app';
import authRoutes from '@/constants/routes/auth';
import { useAuthContext } from '@/context/auth/useAuthContext';
import { Container, HStack, Image, Text, VStack } from '@chakra-ui/react';
import { useNavigate } from '@tanstack/react-router';
import React, { useEffect, useState } from 'react';
import AuthHeader from './variants/AuthHeader';
import DefaultHeader from './variants/DefaultHeader';
import useIsWeb from '@/hooks/useIsWeb';
import { useAppStore } from '@/store/useAppStore';
import { hasUnacceptedNonContentPolicyDocument } from '@/utils/onboardingUtils';
import { DashboardTabsSlot } from './DashboardTabsHost';

type Props = {
  variant?: 'transparent' | 'default';
  backgroundColor?: string;
  isSticky?: boolean;
  hideButtons?: boolean;
  centeredLogo?: boolean;
};

const Header = ({
  variant = 'default',
  backgroundColor = 'transparent',
  isSticky = true,
  hideButtons = false,
  centeredLogo = false,
}: Props) => {
  const isWeb = useIsWeb();

  const isTransparent = variant === 'transparent';
  const logoPath = isTransparent
    ? isWeb
      ? logoVariants.white252x80
      : logoVariants.whiteNoText160x160
    : isWeb
      ? logoVariants.purple252x80
      : logoVariants.purpleNoText160x160;

  const { isAuthenticated, isSuperAdmin, isContentCreator, user } = useAuthContext();
  const legalAcceptanceStatuses = useAppStore((state) => state.legalAcceptanceStatuses);
  const navigate = useNavigate();
  const isContentCreatorOnly = isContentCreator && user?.roles.length === 1;
  const hasPendingEulaUpdate = hasUnacceptedNonContentPolicyDocument(legalAcceptanceStatuses);

  const [scrollPosition, setScrollPosition] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  const handleScroll = () => {
    const currentScrollPos = window.pageYOffset;

    // Check if scrolling down or up
    if (currentScrollPos > scrollPosition) {
      setIsVisible(false); // Hide on scroll down
    } else {
      setIsVisible(true); // Show on scroll up
    }

    setScrollPosition(currentScrollPos);
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollPosition]);

  return (
    <VStack width={'100%'}>
      {isSuperAdmin && (
        <VStack
          bg="extra.lightRed"
          position={'relative'}
          bottom={0}
          height={'40px'}
          width={'100%'}
          justifyContent={'center'}
          alignItems={'center'}
        >
          <Text color={'white'} fontWeight={'bold'}>
            Welcome, Super Admin! You have access to all features with read-only permissions.
          </Text>
        </VStack>
      )}
      <Container
        as={'header'}
        transition="transform 0.3s ease-in-out"
        transform={isVisible ? 'translateY(0)' : 'translateY(-100%)'}
        maxWidth={MAX_CONTAINER_WIDTH}
        width={'100%'}
        top={0}
        zIndex={3}
        position={isSticky ? 'relative' : 'fixed'}
        paddingTop={isWeb && centeredLogo ? '40px' : '20px'}
        paddingX={0}
        backgroundColor={backgroundColor}
      >
        <HStack
          position={'relative'}
          paddingX={isWeb ? 0 : 10}
          justifyContent={centeredLogo ? 'center' : 'space-between'}
          alignItems={'center'}
        >
          <Image
            src={logoPath}
            alt="my-omnia"
            _hover={{ cursor: 'pointer' }}
            width={isWeb ? '126px' : '40px'}
            height={'40px'}
            onClick={() => {
              if (hasPendingEulaUpdate) return;

              navigate({
                to: isAuthenticated
                  ? isContentCreatorOnly
                    ? contentCreatorRoutes.creatorDashboard
                    : appRoutes.userDashboard
                  : authRoutes.signIn,
              });
            }}
          />
          {isAuthenticated && isWeb && !hideButtons && <DashboardTabsSlot />}
          {!hideButtons && (
            <HStack alignItems={'center'}>
              {!isAuthenticated && <AuthHeader />}
              {isAuthenticated && <DefaultHeader isTransparent={isTransparent} />}
            </HStack>
          )}
        </HStack>
      </Container>
    </VStack>
  );
};

export default Header;
